import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, API_BASE, type PptxTemplateRow } from '../../lib/api'
import { useAuth } from '../../lib/auth'
import { Button } from '../../components/ui/Button'
import { Card, CardContent } from '../../components/ui/Card'
import { Select } from '../../components/ui/Select'
import { Label } from '../../components/ui/Label'

const AUDIENCES: PptxTemplateRow['audience'][] = [
  'executive',
  'sales',
  'end_user',
  'training',
]

export function PptxTemplates() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { token } = useAuth()
  const fileRef = useRef<HTMLInputElement>(null)
  const [audience, setAudience] = useState<PptxTemplateRow['audience']>('executive')
  const [uploading, setUploading] = useState(false)

  const { data } = useQuery({
    queryKey: ['admin', 'pptx-templates'],
    queryFn: () => api<PptxTemplateRow[]>('/api/v1/admin/pptx-templates'),
  })

  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/api/v1/admin/pptx-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'pptx-templates'] }),
  })

  async function handleUpload() {
    const file = fileRef.current?.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('audience', audience)
      const res = await fetch(`${API_BASE}/api/v1/admin/pptx-templates`, {
        method: 'POST',
        body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      })
      if (!res.ok) throw new Error(`Upload failed: ${res.status}`)
      if (fileRef.current) fileRef.current.value = ''
      qc.invalidateQueries({ queryKey: ['admin', 'pptx-templates'] })
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold text-ink">
        {t('admin.templates.title')}
      </h1>

      <Card>
        <CardContent className="space-y-3 p-6">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <Label>{t('admin.templates.columns.audience')}</Label>
              <Select
                value={audience}
                onChange={(e) =>
                  setAudience(e.target.value as PptxTemplateRow['audience'])
                }
              >
                {AUDIENCES.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>{t('admin.templates.upload')}</Label>
              <input
                ref={fileRef}
                type="file"
                accept=".pptx"
                className="block w-full rounded-md border border-warm-mist bg-white px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div>
            <Button variant="primary" onClick={handleUpload} disabled={uploading}>
              {uploading ? t('common.saving') : t('admin.templates.upload')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-cream text-xs uppercase text-text-muted">
                <tr>
                  <th className="px-4 py-2 text-left">{t('admin.templates.columns.audience')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.templates.columns.filename')}</th>
                  <th className="px-4 py-2 text-left">{t('admin.templates.columns.uploaded')}</th>
                  <th className="px-4 py-2 text-left">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {data.map((tpl) => (
                  <tr key={tpl.id} className="border-t border-warm-mist">
                    <td className="px-4 py-2">{tpl.audience}</td>
                    <td className="px-4 py-2 font-mono text-xs">{tpl.filename}</td>
                    <td className="px-4 py-2 text-xs text-text-muted">
                      {tpl.uploaded_at.slice(0, 10)}
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove.mutate(tpl.id)}
                      >
                        {t('common.remove')}
                      </Button>
                    </td>
                  </tr>
                ))}
                {data.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-text-muted">
                      {t('common.empty')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
